import config from '../../../config';

const cuestionarioTypeLabels = {
  incontinencia: 'Suelo Pelvico',
  vent: 'Ventilacion',
  tesisTorres: 'Tesis Torres',
};

function parseDate(value) {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function calculateAge(dateBirth) {
  if (!dateBirth) {
    return null;
  }

  const birth = new Date(dateBirth);
  if (Number.isNaN(birth.getTime())) {
    return null;
  }

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }

  return age >= 0 ? age : null;
}

function formatPatientLabel(patient) {
  const fullName = [patient.firstname, patient.lastname].filter(Boolean).join(' ').trim();
  return fullName ? `${fullName} (ID ${patient.id})` : `Paciente ${patient.id}`;
}

function formatRecordPatientLabel(item, fallbackLabel) {
  const patient = item.paciente || {};
  const fullName = [patient.firstname, patient.lastname].filter(Boolean).join(' ').trim();
  return fullName ? `${fullName} · ${fallbackLabel}` : fallbackLabel;
}

export function buildDashboardStats({
  activePatients,
  allPatients,
  entrevistas,
  capacidades,
  valoraciones,
  medidas,
  encoders,
  cuestionarios,
  users,
  hasUsersData,
}) {
  const patientsForStats = activePatients.length > 0 ? activePatients : allPatients;
  const totalPatients = allPatients.length || activePatients.length;
  const activeCount = activePatients.length || allPatients.filter((patient) => patient.active !== false).length;
  const inactiveCount = allPatients.length ? allPatients.filter((patient) => patient.active === false).length : 0;

  const ages = patientsForStats
    .map((patient) => calculateAge(patient.dateBirth))
    .filter((age) => typeof age === 'number');

  const averageAge = ages.length
    ? Math.round(ages.reduce((sum, age) => sum + age, 0) / ages.length)
    : null;

  const malePatients = patientsForStats.filter((patient) => Number(patient.sexo) === 1).length;
  const femalePatients = patientsForStats.filter((patient) => Number(patient.sexo) === 2).length;
  const otherPatients = Math.max(patientsForStats.length - malePatients - femalePatients, 0);

  const groupAccumulator = patientsForStats.reduce((acc, patient) => {
    const key = patient.grupo !== undefined && patient.grupo !== null ? String(patient.grupo) : 'sin-grupo';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const groupBreakdown = Object.entries(groupAccumulator)
    .map(([key, count]) => {
      if (key === 'sin-grupo') {
        return { key, name: 'Sin grupo asignado', count };
      }

      const groupId = Number(key);
      return {
        key,
        name: config.GRUPOS[groupId] || `Grupo ${groupId}`,
        count,
      };
    })
    .sort((a, b) => b.count - a.count);

  const cuestionariosByType = Object.entries(
    cuestionarios.reduce((acc, item) => {
      const label = cuestionarioTypeLabels[item.tipo] || item.tipo || `Categoria ${item.categoria}`;
      acc[label] = (acc[label] || 0) + 1;
      return acc;
    }, {})
  )
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);

  const recentRecords = [];
  const recentPatients = allPatients.length ? allPatients : patientsForStats;

  recentPatients.forEach((patient) => {
    const date = parseDate(patient.createdAt);
    if (date) {
      recentRecords.push({
        type: 'Paciente',
        label: formatPatientLabel(patient),
        date,
      });
    }
  });

  entrevistas.forEach((item) => {
    const date = parseDate(item.createdAt);
    if (date) {
      const interviewType = item.tipoEntrevista ? `Codigo ${item.codigo} · ${item.tipoEntrevista}` : `Codigo ${item.codigo}`;
      recentRecords.push({
        type: 'Entrevista',
        label: formatRecordPatientLabel(item, interviewType),
        date,
      });
    }
  });

  capacidades.forEach((item) => {
    const date = parseDate(item.createdAt);
    if (date) {
      recentRecords.push({
        type: 'SPPB / TUG',
        label: formatRecordPatientLabel(item, `Codigo ${item.codigo}`),
        date,
      });
    }
  });

  valoraciones.forEach((item) => {
    const date = parseDate(item.createdAt);
    if (date) {
      recentRecords.push({
        type: 'Fuerza Presion',
        label: formatRecordPatientLabel(item, `Codigo ${item.codigo}`),
        date,
      });
    }
  });

  medidas.forEach((item) => {
    const date = parseDate(item.createdAt);
    if (date) {
      recentRecords.push({
        type: 'Antropometria',
        label: formatRecordPatientLabel(item, `Codigo ${item.codigo}`),
        date,
      });
    }
  });

  encoders.forEach((item) => {
    const date = parseDate(item.createdAt);
    if (date) {
      recentRecords.push({
        type: '5STS / Encoder',
        label: formatRecordPatientLabel(item, `Codigo ${item.codigo}`),
        date,
      });
    }
  });

  cuestionarios.forEach((item) => {
    const date = parseDate(item.createdAt);
    if (date) {
      const labelType = cuestionarioTypeLabels[item.tipo] || item.tipo || 'Cuestionario';
      recentRecords.push({
        type: 'Cuestionario',
        label: formatRecordPatientLabel(item, labelType),
        date,
      });
    }
  });

  recentRecords.sort((a, b) => b.date.getTime() - a.date.getTime());

  return {
    totalPatients,
    activePatients: activeCount,
    inactivePatients: inactiveCount,
    averageAge,
    malePatients,
    femalePatients,
    otherPatients,
    totalInterviews: entrevistas.length,
    totalCapacidades: capacidades.length,
    totalValoraciones: valoraciones.length,
    totalMedidas: medidas.length,
    totalEncoders: encoders.length,
    totalCuestionarios: cuestionarios.length,
    cuestionariosByType,
    groupBreakdown,
    recentRecords: recentRecords.slice(0, 10),
    totalUsers: hasUsersData ? users.length : null,
    activeUsers: hasUsersData ? users.filter((user) => user.active !== false).length : null,
  };
}
